import React from "react";
import { Tabs, Text } from "@mantine/core";

import debounce from "lodash/debounce";

import { useMiddleware } from "@piqnt/runtime-core/react/ReactMount";
import { OutlineContext } from "./OutlineContext";

import { getKey } from "@piqnt/runtime-core/common/Key";

import { WatchVec2 } from "@piqnt/runtime-core/react/WatchValue";
import {
  Pair,
  PairKey,
  PairValue,
  Subtree,
  SubtreeBody,
  SubtreeHeader,
} from "@piqnt/runtime-core/react/Subtree";

import { LiveBody } from "./LiveBody";
import { LiveFixture } from "./LiveFixture";
import { LiveJoint } from "./LiveJoint";
import { Body, Fixture, Joint, World } from "planck";

export const WorldSubtree = () => {
  const { context, emit } = useMiddleware<OutlineContext>();
  const world = context.world.value;
  const [, setReload] = React.useState(0);

  React.useEffect(() => {
    const forceReload = debounce(() => {
      setReload(Math.random());
    });
    if (world) {
      world.on("add-body", forceReload);
      world.on("remove-body", forceReload);
      world.on("add-fixture", forceReload);
      world.on("remove-fixture", forceReload);
      world.on("add-joint", forceReload);
      world.on("remove-joint", forceReload);
    }
    return () => {
      world.off("add-body", forceReload);
      world.off("remove-body", forceReload);
      world.off("add-fixture", forceReload);
      world.off("remove-fixture", forceReload);
      world.off("add-joint", forceReload);
      world.off("remove-joint", forceReload);
    };
  }, [world]);

  if (!world) return null;

  const bodies = bodyArray(world);
  const joints = jointArray(world);

  return (
    <Tabs defaultValue="bodies">
      <Tabs.List>
        <Tabs.Tab value="bodies">
          <Text size="xs">Bodies</Text>
        </Tabs.Tab>
        <Tabs.Tab value="joints">
          <Text size="xs">Joints</Text>
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="bodies">
        <Subtree open>
          <SubtreeHeader>bodies {bodies.length}</SubtreeHeader>
          <SubtreeBody>
            {bodies.map((body) => (
              <Subtree key={getKey(body)}>
                <SubtreeHeader
                  onClick={() => emit("select-body", { body: getKey(body) })}
                >
                  <LiveBody body={body} />
                </SubtreeHeader>
                <SubtreeBody>
                  {fixtureArray(body).map((fixture) => (
                    <Subtree>
                      <SubtreeHeader
                        onClick={() =>
                          emit("select-fixture", {
                            fixture: getKey(fixture),
                            body: getKey(body),
                          })
                        }
                      >
                        <LiveFixture fixture={fixture} />
                      </SubtreeHeader>
                    </Subtree>
                  ))}
                </SubtreeBody>
              </Subtree>
            ))}
          </SubtreeBody>
        </Subtree>
      </Tabs.Panel>
      <Tabs.Panel value="joints">
        <Subtree open>
          <SubtreeHeader>joints {joints.length}</SubtreeHeader>
          <SubtreeBody>
            {joints.map((joint) => (
              <Subtree key={getKey(joint)}>
                <SubtreeHeader
                  onClick={() => emit("select-joint", { joint: getKey(joint) })}
                >
                  <LiveJoint joint={joint} />
                </SubtreeHeader>
              </Subtree>
            ))}
          </SubtreeBody>
        </Subtree>
      </Tabs.Panel>
    </Tabs>
  );
};

export const WorldEdit = () => {
  const { context } = useMiddleware<OutlineContext>();
  const editable = context.editable.value;
  const world = context.world.value;

  if (!world) return null;

  return (
    <>
      <Subtree open>
        <SubtreeHeader>world</SubtreeHeader>
        <SubtreeBody>
          <Pair>
            <PairKey>gravity</PairKey>
            <PairValue>
              <WatchVec2
                editable={editable}
                getValue={() => world.getGravity()}
              />
            </PairValue>
          </Pair>
        </SubtreeBody>
      </Subtree>
    </>
  );
};

export function bodyArray(world: World) {
  const list: Body[] = [];
  for (let body = world.getBodyList(); body; body = body.getNext()) {
    list.push(body);
  }
  return list;
}

export function fixtureArray(body: Body) {
  const list: Fixture[] = [];
  for (let fix = body.getFixtureList(); fix; fix = fix.getNext()) {
    list.push(fix);
  }
  return list;
}

export function jointArray(world: World) {
  const list: Joint[] = [];
  for (let joint = world.getJointList(); joint; joint = joint.getNext()) {
    list.push(joint);
  }
  return list;
}
